import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { LOGO_URL, CONTACT } from "@/config";
import { useI18n } from "@/i18n";

const LangSwitch = ({ lang, setLang, testid }) => (
  <div data-testid={testid} className="inline-flex items-center rounded-md border border-navy/15 overflow-hidden text-sm font-semibold">
    <button
      data-testid={`${testid}-sk`}
      onClick={() => setLang("sk")}
      className={`px-2.5 py-1 transition-colors ${lang === "sk" ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}
    >SK</button>
    <button
      data-testid={`${testid}-en`}
      onClick={() => setLang("en")}
      className={`px-2.5 py-1 transition-colors ${lang === "en" ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}
    >EN</button>
  </div>
);

export const Navbar = () => {
  const { t, lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const LINKS = [
    { label: t.nav.home, href: "#home" },
    { label: t.nav.services, href: "#services" },
    { label: t.nav.gallery, href: "#gallery" },
    { label: t.nav.reviews, href: "#reviews" },
    { label: t.nav.about, href: "#about" },
    { label: t.nav.contact, href: "#contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-8 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-navy/10 shadow-sm" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <a href="#home" onClick={(e) => { e.preventDefault(); go("#home"); }} data-testid="nav-logo" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Sedlo Services logo" className="h-[40px] sm:h-[48px] lg:h-[54px] w-auto" />
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="text-sm font-medium text-navy/80 hover:text-brand transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand transition-all group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <LangSwitch lang={lang} setLang={setLang} testid="lang-switch-desktop" />
          <a href={CONTACT.phoneHref} data-testid="nav-call-btn" className="flex items-center gap-2 text-sm font-semibold text-navy hover:text-brand transition-colors">
            <Phone className="w-4 h-4" />
            {CONTACT.phone}
          </a>
          <button
            data-testid="nav-book-btn"
            onClick={() => go("#booking")}
            className="btn-lift bg-brand hover:bg-brand-hover text-white font-semibold text-sm px-5 py-2.5 rounded-md shadow-md shadow-brand/20"
          >
            {t.nav.book}
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LangSwitch lang={lang} setLang={setLang} testid="lang-switch-mobile" />
          <button data-testid="nav-menu-toggle" className="text-navy p-2" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-navy/10 px-4 py-4 space-y-1">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`mobile-nav-link-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="block w-full text-left py-3 px-2 text-navy font-medium hover:bg-navy/5 rounded-md"
            >
              {l.label}
            </button>
          ))}
          <button data-testid="mobile-book-btn" onClick={() => go("#booking")} className="btn-lift w-full mt-2 bg-brand text-white font-semibold py-3 rounded-md">
            {t.nav.book}
          </button>
        </div>
      )}
    </header>
  );
};
